/**
 * Urban Customz — audience mode (Personal | Business).
 *
 * The active mode is decided by the server from the URL. This script only:
 *   1. remembers the last mode the visitor chose on purpose (localStorage),
 *   2. points the logo at the business homepage while the visitor is in business mode
 *      and has chosen it before (convenience only — never a redirect),
 *   3. reports mode switches and business CTA clicks to the analytics already on the page.
 *
 * GA4 conversions (2026-10-08): quote_submitted -> generate_lead and design_start go through
 * the page's own Google tag (window.gtag, from the Google & YouTube channel) with an
 * allow-listed, non-personal parameter set, then are published with ga4_sent = 1 so the
 * custom pixel does not send them again.
 *
 * Events (window.dataLayer / gtag / Meta pixel / Shopify web pixels):
 *   personal_mode_selected, business_mode_selected, request_quote_clicked,
 *   bulk_orders_clicked, bundles_clicked, signage_clicked, promo_products_clicked,
 *   swag_platform_clicked, fulfillment_clicked, business_contact_clicked
 */
(function () {
  const KEY = 'uc_mode';
  const root = document.querySelector('[data-uc-mode]');
  const currentMode = root ? root.getAttribute('data-uc-mode') : 'personal';

  function readPreference() {
    try {
      return window.localStorage.getItem(KEY);
    } catch (e) {
      return null;
    }
  }
  function writePreference(mode) {
    try {
      window.localStorage.setItem(KEY, mode);
    } catch (e) {
      /* private mode / blocked storage: nothing to remember */
    }
  }

  // GA4 conversion events. The Google & YouTube channel's tag lives in this page as
  // window.gtag for visitors whose analytics consent allows it, so these two events go
  // through it and share the page view's client id and session. The custom pixel
  // "GA4 quote + design events" ignores anything marked ga4_sent and only steps in
  // (from inside Shopify's pixel sandbox) when the tag was not on the page in time.
  const GA4_ID = 'G-C20HD5VSKP';
  const GA4_EVENTS = { quote_submitted: 'generate_lead', design_start: 'design_start' };
  // Non-personal context only; nothing typed into a form is ever on this list.
  const GA4_PARAMS = ['landing_page', 'city', 'province', 'page_type', 'lead_source', 'quote_type', 'quantity_band', 'product_handle', 'needed_by_set', 'locale'];

  function ga4Params(payload) {
    const out = {};
    GA4_PARAMS.forEach((k) => {
      const v = payload[k];
      if (v === undefined || v === null || v === '') return;
      out[k] = String(v).slice(0, 100);
    });
    return out;
  }

  // Calls done(true) once the event went through window.gtag, done(false) if the tag
  // never showed up (consent declined, blocked, or not loaded within 8 s).
  function sendGa4(eventName, params, done) {
    const started = Date.now();
    const attempt = () => {
      if (typeof window.gtag === 'function') {
        try {
          window.gtag('event', eventName, Object.assign({ send_to: GA4_ID }, params));
          done(true);
        } catch (e) {
          done(false);
        }
        return;
      }
      if (Date.now() - started > 8000) {
        done(false);
        return;
      }
      window.setTimeout(attempt, 250);
    };
    attempt();
  }

  function publish(name, payload) {
    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', name, payload);
    } catch (e) {
      /* noop */
    }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) {
      /* noop */
    }
  }

  function track(name, params) {
    const payload = Object.assign({ uc_mode: currentMode, page_path: window.location.pathname }, params || {});
    const ga4Name = GA4_EVENTS[name];
    if (ga4Name) {
      sendGa4(ga4Name, ga4Params(payload), (sent) => {
        payload.ga4_sent = sent ? 1 : 0;
        publish(name, payload);
      });
      return;
    }
    try {
      if (window.google_tag_manager && Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, payload));
      } else if (typeof window.gtag === 'function') {
        window.gtag('event', name, Object.assign({ transport_type: 'beacon' }, payload));
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, payload));
      }
    } catch (e) {
      /* analytics must never break navigation */
    }
    publish(name, payload);
  }

  // 1 + 3: switch clicks
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-uc-mode-link]');
    if (!link) return;
    const target = link.getAttribute('data-uc-mode-link');
    writePreference(target);
    if (target !== currentMode) {
      track(target + '_mode_selected', { from_mode: currentMode, placement: link.closest('.uc-switch')?.className || '' });
    }
  });

  // 3: business CTA clicks
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-uc-track]');
    if (!el) return;
    const extra = { link_url: el.getAttribute('href') || '', link_text: (el.textContent || '').trim().slice(0, 80) };
    const source = el.getAttribute('data-uc-source');
    if (source) extra.source = source;
    track(el.getAttribute('data-uc-track'), extra);
  });

  // Shared with uc-discovery.js so the whole site reports through one pipeline.
  window.ucTrack = track;

  // 2: logo goes back to the business home for visitors who chose business
  const pref = readPreference();
  if (pref === 'business' && currentMode === 'business') {
    const businessUrl = root && root.getAttribute('data-uc-business-url');
    if (businessUrl) {
      document.querySelectorAll('[data-uc-logo]').forEach((a) => a.setAttribute('href', businessUrl));
    }
  }
  document.documentElement.setAttribute('data-uc-pref', pref || 'none');

  // Timed promo popup (Personal pages, see uc-core.css).
  if (document.body.classList.contains('uc-mode-personal')) {
    const start = Date.now();
    const release = () => {
      document.body.classList.add('uc-popup-ready');
      window.removeEventListener('scroll', check);
    };
    // Released once the visitor has scrolled past two screens and spent 20 s on the page,
    // or after 60 s regardless.
    const check = () => {
      if (window.scrollY > window.innerHeight * 2 && Date.now() - start >= 20000) release();
    };
    window.addEventListener('scroll', check, { passive: true });
    window.setTimeout(check, 20000);
    window.setTimeout(release, 60000);
  }
})();
